import { UserRole } from "@features/settings/types/settings.types";
import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import { Button } from "@shared/components/ui/Button";
import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import CategoryFilter from "../components/CategoryFilter";
import FaqFormModal from "../components/FaqFormModal";
import FaqItem from "../components/FaqItem";
import SearchForm from "../components/SearchForm";
import { useFaqs } from "../hooks/useFaqs";
import { Faq, FaqFormData } from "../types/faqs.types";

const FaqsPage: React.FC = () => {
  const {
    filteredFaqs,
    categories,
    isLoading,
    error,
    filterOptions,
    searchFaqs,
    filterByCategory,
    createFaq,
    updateFaq,
    deleteFaq,
    refreshFaqs,
  } = useFaqs();

  const { user } = useProfile();
  const isAdmin = user?.role === UserRole.ADMIN;

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<Faq | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle opening the form modal for creating a new FAQ
  const handleAddFaq = () => {
    setSelectedFaq(null);
    setIsFormModalOpen(true);
  };

  // Handle opening the form modal for editing an existing FAQ
  const handleEditFaq = (faq: Faq) => {
    setSelectedFaq(faq);
    setIsFormModalOpen(true);
  };

  // Handle opening the delete confirmation modal
  const handleDeleteClick = (id: string) => {
    const faqToDelete = filteredFaqs.find((faq) => faq.id === id);
    if (faqToDelete) {
      setSelectedFaq(faqToDelete);
      setIsDeleteModalOpen(true);
    }
  };

  // Handle form submission (create or update)
  const handleFormSubmit = async (data: FaqFormData) => {
    setIsSubmitting(true);
    try {
      if (selectedFaq) {
        await updateFaq(selectedFaq.id, data);
      } else {
        await createFaq(data);
      }
      setIsFormModalOpen(false);
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.error("Failed to save FAQ. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedFaq) return;

    setIsDeleting(true);
    try {
      await deleteFaq(selectedFaq.id);
      setIsDeleteModalOpen(false);
      toast.success("FAQ deleted successfully");
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Failed to delete FAQ. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="max-w-3xl m-auto">
          {/* Page title and admin actions */}
          <div className="mb-5 flex justify-between items-center">
            <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
              👋 How can we help you today?
            </h1>
            {isAdmin && <Button onClick={handleAddFaq}>Add FAQ</Button>}
          </div>

          {/* Search form */}
          <div className="mb-6">
            <SearchForm
              onSearch={searchFaqs}
              initialValue={filterOptions.searchTerm}
              placeholder="Search for FAQs..."
            />
          </div>

          {/* Filters */}
          {categories.length > 0 && (
            <CategoryFilter
              categories={categories}
              selectedCategory={filterOptions.selectedCategory}
              onSelectCategory={filterByCategory}
            />
          )}

          {/* FAQs list */}
          <div className="mt-8">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  Loading FAQs...
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-500">{error}</p>
                <Button
                  onClick={refreshFaqs}
                  variant="secondary"
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            ) : filteredFaqs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {filterOptions.searchTerm || filterOptions.selectedCategory
                    ? "No FAQs match your search criteria."
                    : "No FAQs available yet."}
                </p>
                {(filterOptions.searchTerm ||
                  filterOptions.selectedCategory) && (
                    <Button
                      onClick={() => {
                        searchFaqs("");
                        filterByCategory(null);
                      }}
                      variant="secondary"
                      className="mt-2"
                    >
                      Clear Filters
                    </Button>
                  )}
              </div>
            ) : (
              <>
                <h2 className="text-2xl text-gray-800 dark:text-gray-100 font-bold mb-4">
                  {filterOptions.selectedCategory
                    ? `${filterOptions.selectedCategory} FAQs`
                    : filterOptions.searchTerm
                      ? "Search Results"
                      : "Frequently Asked Questions"}
                </h2>
                {filteredFaqs
                  .sort((a, b) => a.title.localeCompare(b.title))
                  .map((faq) => (
                    <FaqItem
                      key={faq.id}
                      faq={faq}
                      onEdit={isAdmin ? handleEditFaq : undefined}
                      onDelete={isAdmin ? handleDeleteClick : undefined}
                    />
                  ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <FaqFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSubmit={handleFormSubmit}
          faq={selectedFaq}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        title="Delete Resource"
        message="Are you sure you want to delete this FAQ? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </AuthenticatedLayout>
  );
};

export default FaqsPage;
